import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";

// Bespoke "Midnight IDE" landing for Python Compiler IDE & Editor (Android).
// Dark premium code-editor aesthetic, Python blue + yellow, a live animated REPL
// that types real Python and prints output. Rigorous SEO (meta + OG + JSON-LD).
// Hosted at /apps/pyvedit. Assets in /pyvedit/landing.

const PLAY = "https://play.google.com/store/apps/details?id=com.cosmovex.pyvedit";
const PY_BLUE = "#0F6BBF";
const PY_BLUE_D = "#D9A32E";
const PY_YELLOW = "#A8720E";
const BG = "#FBFAF6";
const PANEL = "#FFFFFF";
const INK = "#26313B";
const MUTE = "#5B6B7C";
const GREEN = "#2E7D4F";
const B = "/pyvedit/landing";
const SANS = `-apple-system, "SF Pro Display", "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
const MONO = `"SF Mono", "JetBrains Mono", "Fira Code", ui-monospace, Menlo, Consolas, monospace`;

const playClick = (e: React.MouseEvent) => {
  e.preventDefault();
  const w = window as any;
  if (w.gtag_report_conversion) w.gtag_report_conversion(PLAY);
  else window.open(PLAY, "_blank", "noopener");
};

const PlayButton: React.FC<{ small?: boolean }> = ({ small }) => (
  <a href={PLAY} onClick={playClick} aria-label="Get Python Compiler IDE & Editor on Google Play"
    style={{
      display: "inline-flex", alignItems: "center", gap: "0.6rem",
      padding: small ? "0.7rem 1.3rem" : "1.05rem 2rem",
      background: "#E8B44C", color: "#221A06",
      borderRadius: "0.9rem", fontWeight: 700, fontSize: small ? "0.98rem" : "1.18rem",
      textDecoration: "none", border: `1px solid ${PY_BLUE_D}`,
      boxShadow: `0 1px 2px rgba(52,44,26,0.06), 0 4px 14px rgba(52,44,26,0.05)`, fontFamily: SANS,
    }}>
    <svg width={small ? 18 : 22} height={small ? 18 : 22} viewBox="0 0 24 24" fill="currentColor">
      <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.2l2.807 1.626a1 1 0 010 1.734l-2.808 1.626L15.206 12l2.492-2.493zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
    </svg>
    Get it on Google Play
  </a>
);

// ── Live animated REPL ──────────────────────────────────────────────────────
const SNIPPETS = [
  { code: 'print("hello, phone")', out: "hello, phone" },
  { code: "sum(range(101))", out: "5050" },
  { code: "[x*x for x in range(6)]", out: "[0, 1, 4, 9, 16, 25]" },
  { code: "import numpy as np; np.arange(5) + 10", out: "array([10, 11, 12, 13, 14])" },
];

// Real, query-matched FAQs (kept truthful per the package audit) — also emitted
// as FAQPage structured data for rich results.
const FAQS = [
  { q: "Can I run Python offline on my Android phone?", a: "Yes. pyvedit runs Python 3.12 fully on your device using a built-in interpreter — no internet, no server and no account required. Write, run and debug Python anywhere." },
  { q: "Is pyvedit free?", a: "Yes, it is free to download and use. An optional one-time Pro purchase removes ads and unlocks extra libraries — there is no subscription." },
  { q: "Does it support numpy and pandas?", a: "Yes. numpy, pandas and matplotlib are built in and free, and they work completely offline, so you can do real data-science practice on your phone." },
  { q: "Can I install pip packages like PyPDF2?", a: "You can install pure-Python packages at runtime with pip (for example PyPDF2 and fpdf2). Packages that need compiled C/C++ extensions, such as scipy or tensorflow, can't run on-device and aren't supported." },
  { q: "Do I need a laptop or PC to use it?", a: "No. pyvedit is a complete Python environment for your phone or tablet — editor, REPL, notebook and console — so you can learn and build without a computer." },
  { q: "Does pyvedit have a REPL and notebook mode?", a: "Yes. It includes an interactive Python REPL, a notebook mode that runs code cell by cell, and a multi-file editor with syntax highlighting." },
  { q: "Can I plot charts with matplotlib?", a: "Yes. matplotlib is bundled and free, so you can generate plots and charts directly on your device, even with no internet connection." },
];

const Repl: React.FC = () => {
  const [idx, setIdx] = useState(0);
  const [typed, setTyped] = useState("");
  const [showOut, setShowOut] = useState(false);

  useEffect(() => {
    const snip = SNIPPETS[idx];
    const timers: number[] = [];
    let i = 0;
    setTyped("");
    setShowOut(false);
    const step = () => {
      if (i <= snip.code.length) {
        setTyped(snip.code.slice(0, i));
        i += 1;
        timers.push(window.setTimeout(step, 48));
      } else {
        timers.push(window.setTimeout(() => setShowOut(true), 320));
        timers.push(window.setTimeout(() => setIdx((p) => (p + 1) % SNIPPETS.length), 2700));
      }
    };
    timers.push(window.setTimeout(step, 250));
    return () => timers.forEach((t) => clearTimeout(t));
  }, [idx]);

  return (
    <div style={{
      background: "#F1EEE5", borderRadius: 14, border: "1px solid #E1DCCE",
      boxShadow: `0 1px 2px rgba(52,44,26,0.06), 0 4px 14px rgba(52,44,26,0.05)`,
      overflow: "hidden", fontFamily: MONO, width: "100%", maxWidth: 460,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "0.7rem 0.9rem", borderBottom: "1px solid #E1DCCE", background: "#F1EEE5" }}>
        <span style={{ width: 11, height: 11, borderRadius: 99, background: "#FF5F56" }} />
        <span style={{ width: 11, height: 11, borderRadius: 99, background: "#FFBD2E" }} />
        <span style={{ width: 11, height: 11, borderRadius: 99, background: "#27C93F" }} />
        <span style={{ marginLeft: 8, color: MUTE, fontSize: "0.78rem" }}>python 3.12 · offline</span>
      </div>
      <div style={{ padding: "1.1rem 1.1rem 1.3rem", fontSize: "0.98rem", lineHeight: 1.7, minHeight: 132 }}>
        <div style={{ color: MUTE }}>Python 3.12 · pyvedit REPL</div>
        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          <span style={{ color: PY_BLUE, fontWeight: 700 }}>{">>> "}</span>
          <span style={{ color: INK }}>{typed}</span>
          <span className="pv-caret" style={{ color: PY_YELLOW }}>▌</span>
        </div>
        {showOut && <div style={{ color: GREEN }}>{SNIPPETS[idx].out}</div>}
      </div>
    </div>
  );
};

const Phone: React.FC<{ src: string; alt: string; w?: number }> = ({ src, alt, w = 250 }) => (
  <div style={{
    width: w, borderRadius: w * 0.12, background: "#05070b", padding: w * 0.022,
    border: "1px solid rgba(38,49,59,0.10)",
    boxShadow: `0 30px 60px -18px rgba(52,44,26,0.20), 0 10px 24px rgba(52,44,26,0.10)`,
  }}>
    <div style={{ borderRadius: w * 0.1, overflow: "hidden", background: "#05070b", lineHeight: 0 }}>
      <img src={src} alt={alt} loading="lazy" style={{ width: "100%", height: "auto", display: "block" }} />
    </div>
  </div>
);

const FeatureCard: React.FC<{ src: string; label: string; sub: string; glow: string }> = ({ src, label, sub, glow }) => (
  <div style={{
    display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
    background: PANEL, border: "1px solid #E1DCCE", borderRadius: "1.5rem", padding: "2rem 1.3rem 1.7rem",
    boxShadow: "0 1px 2px rgba(52,44,26,0.06), 0 4px 14px rgba(52,44,26,0.05)",
  }}>
    <div style={{
      background: glow,
      borderRadius: "1.4rem", padding: "1.6rem 1rem 0", width: "100%",
      display: "flex", justifyContent: "center", overflow: "hidden", maxHeight: 400,
    }}>
      <Phone src={src} alt={label} w={210} />
    </div>
    <h3 style={{ fontFamily: SANS, fontWeight: 700, fontSize: "1.4rem", color: INK, margin: "1.3rem 0 0.4rem" }}>{label}</h3>
    <p style={{ fontFamily: SANS, fontSize: "1rem", color: MUTE, lineHeight: 1.5, margin: 0, maxWidth: 300 }}>{sub}</p>
  </div>
);

const Section: React.FC<{ title: React.ReactNode; sub: string; children: React.ReactNode }> = ({ title, sub, children }) => (
  <section style={{ maxWidth: 1180, margin: "0 auto", padding: "3.5rem 1.5rem" }}>
    <h2 style={{ fontFamily: SANS, fontWeight: 800, fontSize: "clamp(2rem,5vw,3rem)", letterSpacing: "-1.5px", color: INK, margin: 0 }}>{title}</h2>
    <p style={{ fontFamily: SANS, fontSize: "1.18rem", color: MUTE, margin: "0.8rem 0 2.6rem", maxWidth: 640, lineHeight: 1.5 }}>{sub}</p>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.6rem" }}>{children}</div>
  </section>
);

const GradWord: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ color: "#A8720E" }}>{children}</span>
);

const PyVEditLanding: React.FC = () => {
  const [showBar, setShowBar] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowBar(window.scrollY > 640);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Python Compiler IDE & Editor",
    operatingSystem: "Android 7.0+",
    applicationCategory: "DeveloperApplication",
    applicationSubCategory: "Python IDE",
    softwareVersion: "1.2",
    url: "https://cosmovexapps.com/apps/pyvedit",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    description: "Run Python 3 on your Android phone — fully offline. Code editor, interactive REPL, notebook mode and console, with numpy, pandas and matplotlib built in for free. One-time Pro, no subscription.",
    featureList: [
      "Run Python 3.12 offline on Android",
      "Interactive REPL (Python shell)",
      "Notebook mode",
      "numpy, pandas & matplotlib built in and free",
      "Install pure-Python packages with pip",
      "Multi-file projects with syntax highlighting",
    ],
    downloadUrl: PLAY,
    image: "https://cosmovexapps.com" + B + "/og.png",
    publisher: { "@type": "Organization", name: "Cosmovex", url: "https://cosmovexapps.com" },
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://cosmovexapps.com/" },
      { "@type": "ListItem", position: 2, name: "Apps", item: "https://cosmovexapps.com/apps" },
      { "@type": "ListItem", position: 3, name: "Python Compiler IDE & Editor", item: "https://cosmovexapps.com/apps/pyvedit" },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Python Compiler & IDE for Android — Run Python 3 Offline</title>
        <meta name="description" content="Run Python 3 on your phone, fully offline. Code editor, REPL, notebook mode and console — with numpy, pandas & matplotlib built in for free. One-time Pro, no subscription." />
        <link rel="canonical" href="https://cosmovexapps.com/apps/pyvedit" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Python Compiler & IDE for Android" />
        <meta property="og:description" content="Run Python 3 on your phone — editor, REPL, notebook, numpy & pandas built in. Free & offline, no subscription." />
        <meta property="og:image" content={"https://cosmovexapps.com" + B + "/og.png"} />
        <meta property="og:url" content="https://cosmovexapps.com/apps/pyvedit" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="theme-color" content="#FBFAF6" />
        <meta name="keywords" content="python compiler, python ide android, run python on phone, python editor, offline python, python repl, numpy pandas android, learn python mobile" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>

      <div style={{ background: BG, minHeight: "100vh", fontFamily: SANS, position: "relative", overflow: "hidden" }}>
        {/* ambient glows */}
        <div aria-hidden style={{ position: "absolute", top: -180, left: -120, width: 520, height: 520, background: "none", pointerEvents: "none" }} />
        <div aria-hidden style={{ position: "absolute", top: 60, right: -140, width: 460, height: 460, background: "none", pointerEvents: "none" }} />

        {/* NAV */}
        <nav style={{ position: "relative", maxWidth: 1180, margin: "0 auto", padding: "1.4rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
            <img src={B + "/logo.png"} alt="Python Compiler IDE & Editor logo" width={42} height={42} style={{ borderRadius: 11 }} />
            <span style={{ fontWeight: 700, fontSize: "1.08rem", color: INK }}>Python Compiler IDE &amp; Editor</span>
          </div>
          <PlayButton small />
        </nav>

        {/* HERO */}
        <header style={{ position: "relative", maxWidth: 1180, margin: "0 auto", padding: "2.5rem 1.5rem 1.5rem", display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: "2.5rem", alignItems: "center" }}
          className="pv-hero">
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", background: "#F1EEE5", color: PY_YELLOW, fontWeight: 700, fontSize: "0.9rem", padding: "0.45rem 0.9rem", borderRadius: 999, border: "1px solid #E1DCCE", marginBottom: "1.3rem", fontFamily: MONO }}>
              {">>> python 3.12 · offline · free"}
            </div>
            <h1 style={{ fontWeight: 800, fontSize: "clamp(2.5rem,6vw,4.2rem)", lineHeight: 1.04, letterSpacing: "-2.5px", color: INK, margin: 0 }}>
              Run <GradWord>Python</GradWord> on your phone.<br />No laptop needed.
            </h1>
            <p style={{ fontSize: "1.3rem", color: "#3D4B59", lineHeight: 1.45, margin: "1.3rem 0 1.8rem", maxWidth: 520 }}>
              A full Python 3 IDE — editor, interactive REPL, notebook and console. <strong style={{ color: INK }}>numpy, pandas &amp; matplotlib built in, free.</strong> Works offline, no account.
            </p>
            <div style={{ marginBottom: "1.8rem", maxWidth: 460 }}><Repl /></div>
            <PlayButton />
            <p style={{ color: MUTE, fontSize: "0.92rem", margin: "0.9rem 0 0", fontFamily: MONO }}># free · works offline · no sign-up</p>
            <p style={{ margin: "0.7rem 0 0", fontSize: "0.95rem" }}>
            </p>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Phone src={B + "/hero_screen.png"} alt="Python editor running code with live console output" w={300} />
          </div>
        </header>

        {/* BASICS */}
        <Section title="A real Python environment in your pocket" sub="Write code, run it instantly, and see output — all on your phone, fully offline.">
          <FeatureCard src={B + "/run.png"} label="Write & run" sub="Syntax highlighting, smart indent, and a one-tap Run with instant console output." glow="#F1EEE5" />
          <FeatureCard src={B + "/repl.png"} label="Interactive REPL" sub="A live Python shell — type a line, run it immediately. Perfect for learning." glow="#F1EEE5" />
          <FeatureCard src={B + "/notebook.png"} label="Notebook mode" sub="Run code cell by cell with output beside each cell, notebook style." glow="#F1EEE5" />
        </Section>

        {/* POWERFUL */}
        <Section title={<>Batteries included <span style={{ color: PY_YELLOW }}>— for real</span></>} sub="The libraries you actually use, bundled and free — plus install more on the fly.">
          <FeatureCard src={B + "/packages.png"} label="numpy & pandas free" sub="numpy, pandas and matplotlib are built in and free — do real data science offline." glow="#F1EEE5" />
          <FeatureCard src={B + "/examples.png"} label="Built-in examples" sub="Ready-to-run sample scripts to learn from and build on, right out of the box." glow="#F1EEE5" />
          <FeatureCard src={B + "/projects.png"} label="Multi-file projects" sub="Organize your code across many .py files with a built-in file manager." glow="#F1EEE5" />
        </Section>

        {/* WHY DIFFERENT */}
        <section style={{ position: "relative", margin: "2rem 1.5rem", padding: "3.5rem 1.5rem", maxWidth: 1100, marginInline: "auto", background: "#F1EEE5", border: "1px solid #E1DCCE", borderRadius: "2rem" }}>
          <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
            <h2 style={{ fontWeight: 800, fontSize: "clamp(1.9rem,5vw,2.9rem)", letterSpacing: "-1.5px", color: "#26313B", margin: 0 }}>
              No subscription. No sign-in.<br />Your code stays on your <GradWord>device</GradWord>.
            </h2>
            <p style={{ fontSize: "1.18rem", color: "#3D4B59", lineHeight: 1.55, margin: "1.3rem auto 2rem", maxWidth: 600 }}>
              Python the way it should be on mobile: open the editor, write code, tap Run. numpy, pandas and matplotlib are built in and free. The Pro upgrade is a one-time purchase that removes ads — never a subscription.
            </p>
            <div style={{ display: "flex", gap: "0.7rem", justifyContent: "center", flexWrap: "wrap", marginBottom: "2.2rem" }}>
              {["numpy & pandas free", "REPL", "Notebook", "Offline", "pip install", "One-time Pro"].map((t) => (
                <span key={t} style={{ background: "#FFFFFF", border: `1px solid #D9A32E`, color: "#26313B", fontWeight: 700, fontSize: "0.95rem", padding: "0.55rem 1.1rem", borderRadius: 999, fontFamily: MONO }}>{t}</span>
              ))}
            </div>
            <PlayButton />
          </div>
        </section>

        {/* FAQ */}
        <section style={{ position: "relative", maxWidth: 880, margin: "0 auto", padding: "3.5rem 1.5rem 1rem" }}>
          <h2 style={{ fontFamily: SANS, fontWeight: 800, fontSize: "clamp(2rem,5vw,3rem)", letterSpacing: "-1.5px", color: INK, margin: 0 }}>
            Frequently asked questions
          </h2>
          <p style={{ fontFamily: SANS, fontSize: "1.18rem", color: MUTE, margin: "0.8rem 0 2.4rem", maxWidth: 600, lineHeight: 1.5 }}>
            Everything about running Python on your phone with pyvedit.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {FAQS.map((f) => (
              <div key={f.q} style={{ background: PANEL, border: "1px solid #E1DCCE", borderRadius: "1rem", padding: "1.3rem 1.5rem", boxShadow: "0 1px 2px rgba(52,44,26,0.06), 0 4px 14px rgba(52,44,26,0.05)" }}>
                <h3 style={{ fontFamily: SANS, fontWeight: 700, fontSize: "1.18rem", color: INK, margin: "0 0 0.5rem", display: "flex", gap: "0.6rem" }}>
                  <span style={{ color: PY_YELLOW, fontFamily: MONO }}>{">>>"}</span>{f.q}
                </h3>
                <p style={{ fontFamily: SANS, fontSize: "1.04rem", color: "#3D4B59", lineHeight: 1.6, margin: 0 }}>{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FOOTER */}
        <footer style={{ position: "relative", maxWidth: 1180, margin: "0 auto", padding: "2.5rem 1.5rem 6rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", color: MUTE, fontSize: "0.93rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <img src={B + "/logo.png"} alt="" width={30} height={30} style={{ borderRadius: 8 }} />
            <span>© Cosmovex · Python Compiler IDE &amp; Editor</span>
          </div>
          <div style={{ display: "flex", gap: "1.4rem" }}>
            <a href="/apps/pyvedit/privacy-policy" style={{ color: MUTE, textDecoration: "none" }}>Privacy</a>
            <a href="/apps/pyvedit/support" style={{ color: MUTE, textDecoration: "none" }}>Support</a>
            <a href={PLAY} onClick={playClick} style={{ color: PY_YELLOW, fontWeight: 700, textDecoration: "none" }}>Get the app</a>
          </div>
        </footer>
      </div>

      {/* STICKY INSTALL BAR */}
      {showBar && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1000, display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.65rem 1rem calc(0.65rem + env(safe-area-inset-bottom))", background: "rgba(251,250,246,0.96)", backdropFilter: "blur(14px)", borderTop: "1px solid #E1DCCE" }}>
          <img src={B + "/logo.png"} alt="" width={38} height={38} style={{ borderRadius: 9 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: "#26313B", fontWeight: 700, fontSize: "0.95rem" }}>Python Compiler IDE &amp; Editor</div>
            <div style={{ color: MUTE, fontSize: "0.78rem", fontFamily: MONO }}>free · offline · numpy & pandas</div>
          </div>
          <PlayButton small />
        </div>
      )}

      <style>{`
        @keyframes pvBlink { 0%,49%{opacity:1} 50%,100%{opacity:0} }
        .pv-caret { animation: pvBlink 1s step-end infinite; }
        @media (max-width: 820px){ .pv-hero{ grid-template-columns: 1fr !important; text-align:center; } .pv-hero .pv-caret{} .pv-hero > div:last-child{ margin-top:1.5rem; } .pv-hero > div:first-child > div{ margin-left:auto; margin-right:auto; } }
      `}</style>
    </>
  );
};

export default PyVEditLanding;
